package com.rylcart.cp.ui.screens

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.rylcart.cp.ui.api.ApiException
import com.rylcart.cp.ui.api.CreateTenantRequest
import com.rylcart.cp.ui.components.PrimaryButton
import com.rylcart.cp.ui.components.SecondaryButton
import com.rylcart.cp.ui.components.TextField
import com.rylcart.cp.ui.state.LocalApp
import com.rylcart.cp.ui.state.Route
import kotlinx.coroutines.launch
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.TextArea
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement
import kotlin.random.Random

// Mirrors the server-side ResourcePlans defaults so the form can show what a
// blank field will resolve to. Triple = (cpus, memoryMb, storageGb).
private val planDefaults = mapOf(
    "starter"    to Triple("1.0", 1024, 10),
    "growth"     to Triple("2.0", 2048, 25),
    "enterprise" to Triple("4.0", 4096, 100),
)

private fun generatePassword(): String {
    val upper = ('A'..'Z').toList()
    val lower = ('a'..'z').toList()
    val digits = ('0'..'9').toList()
    val special = "!@#%^&*".toList()
    val all = upper + lower + digits + special
    // Guarantee at least one of each category, then fill to 20 chars
    val required = listOf(upper.random(Random), lower.random(Random), digits.random(Random), special.random(Random))
    val rest = (1..16).map { all.random(Random) }
    return (required + rest).shuffled(Random).joinToString("")
}

@Composable
fun NewTenantScreen() {
    val app = LocalApp.current
    val scope = rememberCoroutineScope()
    var slug by remember { mutableStateOf("") }
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var host by remember { mutableStateOf("") }
    var plan by remember { mutableStateOf("starter") }
    var deployTarget by remember { mutableStateOf("docker") }
    var adminUsername by remember { mutableStateOf("rylcart_admin") }
    var adminPassword by remember { mutableStateOf("") }
    var showPassword by remember { mutableStateOf(false) }
    var busy by remember { mutableStateOf(false) }

    // Resource caps — blank fields fall back to the plan default server-side.
    var cpuLimit by remember { mutableStateOf("") }
    var memoryLimitMb by remember { mutableStateOf("") }
    var storageLimitGb by remember { mutableStateOf("") }

    // Remote VPS (SSH) connection details.
    var sshHost by remember { mutableStateOf("") }
    var sshUser by remember { mutableStateOf("root") }
    var sshPort by remember { mutableStateOf("22") }
    var sshAuth by remember { mutableStateOf("key") }   // "key" | "password"
    var sshPrivateKey by remember { mutableStateOf("") }
    var sshPassword by remember { mutableStateOf("") }
    var showSshPassword by remember { mutableStateOf(false) }

    val defaults = planDefaults[plan] ?: planDefaults.getValue("starter")

    val rootDomain = window.location.hostname.removePrefix("cp.")
    val derivedHost = if (host.isBlank() && slug.isNotBlank()) "$slug.$rootDomain" else host

    Div(attrs = { classes("row", "between"); style { property("margin-bottom", "20px") } }) {
        H2 { Text("New tenant") }
        SecondaryButton("Cancel") { app.navigate(Route.Tenants) }
    }

    Div(attrs = { classes("card"); style { property("max-width", "640px") } }) {
        H3 { Text("Store") }
        TextField("Store name", name, placeholder = "Acme Quick Mart") { name = it }
        TextField(
            label = "Slug",
            value = slug,
            placeholder = "acme",
            hint = "Lowercase, dashes ok. Drives the host: $derivedHost.",
        ) { slug = it.lowercase().filter { c -> c.isLetterOrDigit() || c == '-' } }
        TextField(
            label = "Custom host (optional)",
            value = host,
            placeholder = derivedHost.ifBlank { "store.example.com" },
            hint = "Leave blank to use slug.$rootDomain. DNS must already point to this server.",
        ) { host = it.trim().lowercase() }

        Div(attrs = { classes("field") }) {
            Label { Text("Plan") }
            Select(attrs = {
                onChange { plan = (it.target as HTMLSelectElement).value }
            }) {
                listOf("starter", "growth", "enterprise").forEach { p ->
                    Option(p, attrs = { if (p == plan) attr("selected", "") }) { Text(p) }
                }
            }
        }
    }

    Div(attrs = { classes("card"); style { property("max-width", "640px") } }) {
        H3 { Text("Owner") }
        TextField("Admin email", email, type = InputType.Email, placeholder = "owner@store.com") { email = it }
    }

    Div(attrs = { classes("card"); style { property("max-width", "640px") } }) {
        H3 { Text("WordPress credentials") }

        TextField(
            label = "WP admin username",
            value = adminUsername,
            placeholder = "rylcart_admin",
            hint = "Username for the WordPress admin account.",
        ) { adminUsername = it.trim() }

        // Password row: input + show/hide toggle + generate button
        Div(attrs = { classes("field") }) {
            Label { Text("WP admin password") }
            Div(attrs = { classes("row"); style { property("gap", "8px"); property("align-items", "center") } }) {
                Input(type = if (showPassword) InputType.Text else InputType.Password) {
                    value(adminPassword)
                    placeholder("Leave blank to auto-generate")
                    style { property("flex", "1") }
                    onInput { adminPassword = (it.target as HTMLInputElement).value }
                }
                Button(attrs = {
                    classes("btn", "secondary")
                    attr("type", "button")
                    title(if (showPassword) "Hide password" else "Show password")
                    onClick { showPassword = !showPassword }
                }) { Text(if (showPassword) "Hide" else "Show") }
                Button(attrs = {
                    classes("btn", "secondary")
                    attr("type", "button")
                    title("Generate a random strong password")
                    onClick { adminPassword = generatePassword(); showPassword = true }
                }) { Text("Generate") }
            }
            Div(attrs = { classes("hint") }) {
                Text("Leave blank to have the server auto-generate one (you won't see it — use Generate instead).")
            }
        }
    }

    Div(attrs = { classes("card"); style { property("max-width", "640px") } }) {
        H3 { Text("Deploy target") }
        Div(attrs = { classes("field") }) {
            Label { Text("Where to deploy") }
            Select(attrs = {
                onChange { deployTarget = (it.target as HTMLSelectElement).value }
            }) {
                Option("docker",      attrs = { if (deployTarget == "docker") attr("selected", "") }) { Text("This server (Docker, recommended)") }
                Option("ssh",         attrs = { if (deployTarget == "ssh") attr("selected", "") })    { Text("Remote VPS via SSH") }
                Option("directadmin", attrs = { if (deployTarget == "directadmin") attr("selected", "") }) { Text("DirectAdmin (coming soon)") }
            }
            Div(attrs = { classes("hint") }) {
                Text(when (deployTarget) {
                    "docker" -> "Provisions a Docker stack on this Oracle host. ~60 seconds to live store."
                    "ssh"    -> "SSH into a remote VPS and provision the stack there. The host must already be bootstrapped (Docker + Traefik edge)."
                    else     -> "Will provision via DirectAdmin's HTTPS API. Not wired through CP API yet — falls back to docker."
                })
            }
        }

        // Connection details only matter for the remote-VPS target.
        if (deployTarget == "ssh") {
            TextField(
                label = "VPS host / IP",
                value = sshHost,
                placeholder = "203.0.113.10",
                hint = "Public address of the target server. DNS for the store host must point here.",
            ) { sshHost = it.trim() }
            Div(attrs = { classes("row"); style { property("gap", "12px") } }) {
                Div(attrs = { style { property("flex", "2") } }) {
                    TextField("SSH user", sshUser, placeholder = "root") { sshUser = it.trim() }
                }
                Div(attrs = { style { property("flex", "1") } }) {
                    TextField("SSH port", sshPort, placeholder = "22") {
                        sshPort = it.filter { c -> c.isDigit() }
                    }
                }
            }
            Div(attrs = { classes("field") }) {
                Label { Text("Authentication") }
                Select(attrs = {
                    onChange { sshAuth = (it.target as HTMLSelectElement).value }
                }) {
                    Option("key",      attrs = { if (sshAuth == "key") attr("selected", "") })      { Text("Private key (recommended)") }
                    Option("password", attrs = { if (sshAuth == "password") attr("selected", "") }) { Text("Password") }
                }
            }

            if (sshAuth == "key") {
                Div(attrs = { classes("field") }) {
                    Label { Text("SSH private key") }
                    TextArea(value = sshPrivateKey, attrs = {
                        classes("input")
                        attr("rows", "6")
                        placeholder("-----BEGIN OPENSSH PRIVATE KEY-----\n...")
                        style { property("width", "100%"); property("font-family", "monospace"); property("font-size", "12px") }
                        onInput { sshPrivateKey = it.value }
                    })
                    Div(attrs = { classes("hint") }) {
                        Text("Stored encrypted (AES-GCM) and used only to connect during provisioning. Use a dedicated deploy key with no passphrase.")
                    }
                }
            } else {
                Div(attrs = { classes("field") }) {
                    Label { Text("SSH password") }
                    Div(attrs = { classes("row"); style { property("gap", "8px"); property("align-items", "center") } }) {
                        Input(type = if (showSshPassword) InputType.Text else InputType.Password) {
                            value(sshPassword)
                            placeholder("Password for $sshUser@${sshHost.ifBlank { "host" }}")
                            style { property("flex", "1") }
                            onInput { sshPassword = (it.target as HTMLInputElement).value }
                        }
                        Button(attrs = {
                            classes("btn", "secondary")
                            attr("type", "button")
                            title(if (showSshPassword) "Hide password" else "Show password")
                            onClick { showSshPassword = !showSshPassword }
                        }) { Text(if (showSshPassword) "Hide" else "Show") }
                    }
                    Div(attrs = { classes("hint") }) {
                        Text("Stored encrypted (AES-GCM). Requires sshpass on the control-plane host. A key is more secure where possible.")
                    }
                }
            }
        }
    }

    Div(attrs = { classes("card"); style { property("max-width", "640px") } }) {
        H3 { Text("Resource allocation") }
        P(attrs = { classes("hint") }) {
            Text("Caps for this tenant's whole stack so one store can't exhaust the server. " +
                "Leave blank to use the ${plan} plan defaults (${defaults.first} CPU / ${defaults.second} MB / ${defaults.third} GB).")
        }
        Div(attrs = { classes("row"); style { property("gap", "12px") } }) {
            Div(attrs = { style { property("flex", "1") } }) {
                TextField(
                    label = "CPU cores",
                    value = cpuLimit,
                    placeholder = defaults.first,
                    hint = "Fractional ok. Min 0.25.",
                ) { cpuLimit = it.filter { c -> c.isDigit() || c == '.' } }
            }
            Div(attrs = { style { property("flex", "1") } }) {
                TextField(
                    label = "Memory (MB)",
                    value = memoryLimitMb,
                    placeholder = defaults.second.toString(),
                    hint = "Min 512.",
                ) { memoryLimitMb = it.filter { c -> c.isDigit() } }
            }
            Div(attrs = { style { property("flex", "1") } }) {
                TextField(
                    label = "Storage (GB)",
                    value = storageLimitGb,
                    placeholder = defaults.third.toString(),
                    hint = "Advisory quota.",
                ) { storageLimitGb = it.filter { c -> c.isDigit() } }
            }
        }
    }

    val sshCredMissing = if (sshAuth == "key") sshPrivateKey.isBlank() else sshPassword.isBlank()
    val sshIncomplete = deployTarget == "ssh" && (sshHost.isBlank() || sshUser.isBlank() || sshCredMissing)

    Div(attrs = { classes("row"); style { property("max-width", "640px"); property("gap", "12px") } }) {
        PrimaryButton("Create & provision", disabled = busy || slug.isBlank() || name.isBlank() || email.isBlank() || sshIncomplete) {
            busy = true
            scope.launch {
                val req = CreateTenantRequest(
                    slug = slug,
                    name = name,
                    adminEmail = email,
                    host = host.ifBlank { null },
                    plan = plan,
                    deployTarget = deployTarget,
                    adminUsername = adminUsername.ifBlank { null },
                    adminPassword = adminPassword.ifBlank { null },
                    cpuLimit = cpuLimit.ifBlank { null },
                    memoryLimitMb = memoryLimitMb.toIntOrNull(),
                    storageLimitGb = storageLimitGb.toIntOrNull(),
                    sshHost = if (deployTarget == "ssh") sshHost.ifBlank { null } else null,
                    sshUser = if (deployTarget == "ssh") sshUser.ifBlank { null } else null,
                    sshPort = if (deployTarget == "ssh") sshPort.toIntOrNull() else null,
                    sshPrivateKey = if (deployTarget == "ssh" && sshAuth == "key") sshPrivateKey.ifBlank { null } else null,
                    sshPassword = if (deployTarget == "ssh" && sshAuth == "password") sshPassword.ifBlank { null } else null,
                )
                runCatching { app.api.createTenant(req) }
                    .onSuccess {
                        app.showOk("Provisioning started — run #${it.runId}")
                        app.navigate(Route.TenantDetail(it.tenant.id))
                    }
                    .onFailure { app.showError((it as? ApiException)?.message ?: "create failed") }
                busy = false
            }
        }
        SecondaryButton("Discard") { app.navigate(Route.Tenants) }
    }
}
